import { SYSTEM_PROMPTS } from './prompts'

const GROQ_API_KEY = import.meta.env.VITE_GROQ_API_KEY
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions'
const MODEL = 'llama-3.3-70b-versatile'

interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

interface GroqResponse {
  choices: { message: { content: string } }[]
}

async function groqRequest(messages: ChatMessage[], temperature = 0.7): Promise<string> {
  if (!GROQ_API_KEY) throw new Error('Groq API key not configured')

  const response = await fetch(GROQ_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: MODEL,
      messages,
      temperature,
      max_tokens: 2048,
    }),
  })

  if (!response.ok) {
    const err = await response.text()
    throw new Error(`Groq API error: ${err}`)
  }

  const data: GroqResponse = await response.json()
  return data.choices[0]?.message?.content || ''
}

// ---- Core Chat ----

export async function groqChat(
  userMessage: string,
  conversationHistory: ChatMessage[] = [],
): Promise<string> {
  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.chat },
    ...conversationHistory,
    { role: 'user', content: userMessage },
  ]
  return groqRequest(messages)
}

// ---- Streaming Chat ----

export async function groqChatStream(
  userMessage: string,
  conversationHistory: ChatMessage[] = [],
  onChunk: (chunk: string) => void,
  onDone: (fullText: string) => void,
): Promise<void> {
  if (!GROQ_API_KEY) throw new Error('Groq API key not configured')

  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.chat },
    ...conversationHistory,
    { role: 'user', content: userMessage },
  ]

  const response = await fetch(GROQ_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: MODEL,
      messages,
      temperature: 0.7,
      max_tokens: 2048,
      stream: true,
    }),
  })

  if (!response.ok) {
    const err = await response.text()
    throw new Error(`Groq API error: ${err}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let fullText = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const text = decoder.decode(value, { stream: true })
    const lines = text.split('\n').filter((l) => l.startsWith('data: '))

    for (const line of lines) {
      const data = line.slice(6)
      if (data === '[DONE]') continue
      try {
        const parsed = JSON.parse(data)
        const content = parsed.choices?.[0]?.delta?.content
        if (content) {
          fullText += content
          onChunk(content)
        }
      } catch {
        // skip malformed chunks
      }
    }
  }

  onDone(fullText)
}

// ---- Dashboard Insights ----

interface InsightData {
  title: string
  description: string
  severity: 'positive' | 'warning' | 'info'
}

export async function generateInsights(dashboardContext: string): Promise<InsightData[]> {
  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.insights },
    { role: 'user', content: `Generate 3 actionable insights from this business data:\n\n${dashboardContext}` },
  ]
  const response = await groqRequest(messages, 0.5)
  try {
    return JSON.parse(response)
  } catch {
    return [
      { title: 'Revenue Trend', description: 'Analysis complete — your data shows consistent growth patterns across key metrics.', severity: 'positive' as const },
      { title: 'Action Needed', description: 'Review the latest activity data for potential optimization opportunities.', severity: 'info' as const },
      { title: 'Growth Opportunity', description: 'Consider expanding high-performing channels based on current engagement rates.', severity: 'positive' as const },
    ]
  }
}

// ---- CRM AI ----

interface CRMSuggestion {
  emailDraft: string
  closeScore: number
  nextAction: string
}

export async function generateCRMSuggestion(contactData: string): Promise<CRMSuggestion> {
  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.crm },
    { role: 'user', content: `Analyze this contact/deal and provide suggestions:\n\n${contactData}` },
  ]
  const response = await groqRequest(messages, 0.5)
  try {
    return JSON.parse(response)
  } catch {
    return {
      emailDraft: `Hi,\n\nI wanted to follow up on our recent conversation. I believe there's a great opportunity for us to work together.\n\nWould you be available for a quick call this week to discuss next steps?\n\nBest regards`,
      closeScore: 65,
      nextAction: 'Schedule a follow-up call to discuss specific requirements and timeline.',
    }
  }
}

// ---- Marketing AI ----

interface MarketingResult {
  subjectLines?: string[]
  content?: string
  analysis?: string
}

export async function generateMarketingContent(
  action: 'subjects' | 'content' | 'analysis',
  context: string,
): Promise<MarketingResult> {
  const actionPrompts = {
    subjects: `Generate 5 compelling email subject lines for this campaign context. Return as JSON: { "subjectLines": ["..."] }`,
    content: `Generate a short marketing message for this context. Return as JSON: { "content": "..." }`,
    analysis: `Analyze this campaign performance data and suggest improvements. Return as JSON: { "analysis": "..." }`,
  }

  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.marketing },
    { role: 'user', content: `${actionPrompts[action]}\n\nContext: ${context}` },
  ]
  const response = await groqRequest(messages, 0.7)
  try {
    return JSON.parse(response)
  } catch {
    if (action === 'subjects') {
      return {
        subjectLines: [
          'Don\'t miss out — exclusive offer inside',
          'Your business deserves better tools',
          'Quick question about your growth goals',
          'New: AI-powered features just launched',
          'See how others are getting 3x results',
        ],
      }
    }
    return { content: 'Check out our latest updates designed to help your business grow faster with AI-powered tools.', analysis: 'Campaign performance is above average. Consider A/B testing subject lines and optimizing send times.' }
  }
}

// ---- Security AI ----

interface SecurityAnalysis {
  riskSummary: string
  remediationSteps: string[]
  priority: string
  businessImpact: string
}

export async function analyzeVulnerability(vulnData: string): Promise<SecurityAnalysis> {
  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.security },
    { role: 'user', content: `Analyze this vulnerability and provide remediation guidance:\n\n${vulnData}` },
  ]
  const response = await groqRequest(messages, 0.3)
  try {
    return JSON.parse(response)
  } catch {
    return {
      riskSummary: 'This vulnerability poses a significant risk to your application security and should be addressed promptly.',
      remediationSteps: [
        'Review and sanitize all user inputs',
        'Implement parameterized queries',
        'Update security configurations',
        'Run a follow-up security scan',
      ],
      priority: 'high',
      businessImpact: 'Potential data breach, unauthorized access, and compliance violations if left unpatched.',
    }
  }
}

// ---- Enterprise Intelligence AI ----

export async function analyzeBusinessQuery(query: string, context?: string): Promise<string> {
  const userContent = context
    ? `Business context:\n${context}\n\nUser question: ${query}`
    : query

  const messages: ChatMessage[] = [
    { role: 'system', content: SYSTEM_PROMPTS.enterprise },
    { role: 'user', content: userContent },
  ]
  return groqRequest(messages, 0.6)
}
